import { FREE_DAILY_CAP } from "@/lib/plans";

/**
 * The subset of the `/api/credits` (and the daily-augmented `/api/profile/stats`
 * + `/api/billing/history`) payloads that drives every credit display. Kept
 * loose because each endpoint sends a slightly different shape; `deriveCreditView`
 * normalizes them so no surface has to re-derive the daily math itself.
 */
export interface CreditPayload {
  plan?: string | null;
  credits_total?: number | null;
  credits_remaining?: number | null;
  credits_used?: number | null;
  daily_limit?: number | null;
  daily_cap?: number | null;
  daily_used?: number | null;
  daily_resets_at?: string | null;
  resets_at?: string | null;
  free_credits_expired?: boolean | null;
  /**
   * Authoritative monthly credit allocation for the subscription (the upgraded
   * custom total, or the plan default; null for free). Sent by /api/credits,
   * /api/profile/stats, and /api/billing/overview. Threaded through so every
   * surface reads the SAME allocation off the shared view. See
   * lib/credits/monthly-allocation.ts.
   */
  monthly_allocation?: number | null;
  /**
   * Credits RESERVED by build turns that haven't settled yet (/api/credits →
   * credits_on_hold, /api/profile/stats → the same field).
   *
   * `credits_remaining` is ALREADY net of this — reserve_credits writes the hold
   * straight to the ledger — so it must never be subtracted a second time. It is
   * the share of the current drop that is TEMPORARY: settle_credit_task hands
   * back whatever the build didn't use. Threaded onto the shared view so every
   * surface can say "N of this is a hold, not a charge" in the same words. See
   * lib/credits/hold.ts.
   */
  credits_on_hold?: number | null;
  /**
   * Age of the oldest hold behind `credits_on_hold`, whole seconds (null when
   * nothing is held or the endpoint predates the field). Carried ONLY for the
   * copy-integrity tripwire: the server excludes reservations past
   * HOLD_TTL_MINUTES from `credits_on_hold`, so an age at or past the TTL here
   * means a surface was handed a hold the server should never have sent.
   */
  hold_age_seconds?: number | null;
}

export interface CreditView {
  plan: string;
  /**
   * True when the user is a free account inside an active daily-cap window
   * (migration 131): the API sends a non-null daily limit + reset time then,
   * and null for paid plans / expired free windows.
   */
  isFreeDailyCapped: boolean;
  /** Lifetime pool granted, e.g. 300. */
  total: number;
  /** Banked credits still available across all days, e.g. 293. */
  totalRemaining: number;
  /** total − remaining, e.g. 7. */
  usedTotal: number;
  /** Per-day spend cap, e.g. 50; null for uncapped (paid/expired) users. */
  dailyLimit: number | null;
  /** Credits spent so far today, e.g. 31. */
  usedToday: number;
  /**
   * What the user can actually spend right now. For free capped users this is
   * min(dailyLimit − usedToday, totalRemaining) — clamped so a near-empty pool
   * never advertises more "available today" than actually exists (e.g. 19 of a
   * 50/day cap when only 19 total remain). Equals totalRemaining for uncapped
   * users, where there is no daily ceiling.
   */
  availableToday: number;
  /** ISO timestamp the daily allowance resets; null for uncapped users. */
  resetsAtIso: string | null;
  /**
   * The subscription's monthly credit allocation — what the plan grants each
   * cycle (upgraded custom total or plan default). null for free accounts (no
   * subscription allocation) and when the feeding endpoint didn't send it.
   * Distinct from `total` (lifetime/all-sources pool) and `totalRemaining`
   * (live balance). Format for display with
   * lib/credits/monthly-allocation.ts#formatMonthlyAllocation.
   */
  monthlyAllocation: number | null;
  /**
   * Credits held by a build that is running right now and hasn't settled. 0 when
   * nothing is in flight, and 0 on any surface whose endpoint doesn't send the
   * field — which is the honest default: a surface that cannot know about holds
   * must not invent one.
   *
   * NOT a second deduction. `totalRemaining` and `availableToday` are already
   * net of it; this is how much of that drop comes back at settlement. Every
   * number on this view is spendable-right-now truth, and `onHold` is the
   * explanation for why it dipped. Format with lib/credits/hold.ts#formatCreditHold.
   */
  onHold: number;
  /** Age of the oldest hold behind `onHold` (seconds), or null. Telemetry-only
   *  passthrough for the copy-integrity tripwire — never rendered. */
  holdAgeSeconds: number | null;
}

/**
 * Single source of truth for "300 total, 50/day, 19 left today" math. Every
 * credit surface (header chip, usage page, billing page, chat banner) should
 * read from this so they can't drift apart.
 */
export function deriveCreditView(p: CreditPayload): CreditView {
  const plan = p.plan ?? "free";
  const total = Math.max(0, p.credits_total ?? 0);
  const totalRemaining = Math.max(0, p.credits_remaining ?? 0);
  const usedTotal = Math.max(0, p.credits_used ?? total - totalRemaining);

  const dailyLimitRaw = p.daily_limit ?? p.daily_cap ?? null;
  const resetsAtIso = p.daily_resets_at ?? p.resets_at ?? null;
  const isFreeDailyCapped =
    plan === "free" && dailyLimitRaw != null && resetsAtIso != null;

  const dailyLimit = isFreeDailyCapped ? (dailyLimitRaw ?? FREE_DAILY_CAP) : null;
  const usedToday = Math.max(0, p.daily_used ?? 0);
  const availableToday =
    isFreeDailyCapped && dailyLimit != null
      ? Math.max(0, Math.min(dailyLimit - usedToday, totalRemaining))
      : totalRemaining;

  // Only paid accounts have a subscription allocation; free runs on the daily
  // model. Guard on > 0 so a stray 0 never renders as "0 credits/month".
  const monthlyAllocation =
    plan !== "free" &&
    typeof p.monthly_allocation === "number" &&
    p.monthly_allocation > 0
      ? Math.round(p.monthly_allocation)
      : null;

  // Clamped, never subtracted: the balances above are already net of the hold.
  // A hold larger than the pool is possible and legitimate (an initial build
  // holds the full-build ceiling against a nearly-spent pool), so this is
  // deliberately NOT capped at `totalRemaining`.
  const onHold = Math.max(0, Math.round(p.credits_on_hold ?? 0));
  const holdAgeSeconds =
    onHold > 0 && typeof p.hold_age_seconds === "number" && p.hold_age_seconds >= 0
      ? Math.floor(p.hold_age_seconds)
      : null;

  return {
    plan,
    isFreeDailyCapped,
    total,
    totalRemaining,
    usedTotal,
    dailyLimit,
    usedToday,
    availableToday,
    resetsAtIso,
    monthlyAllocation,
    onHold,
    holdAgeSeconds,
  };
}

/**
 * True when the BANKED POOL — not today's cap — is what limits spending, i.e.
 * the pool is at or below today's remaining room.
 *
 * This is the difference between "come back tomorrow" and "you have to
 * upgrade". A daily roll (`_roll_free_daily_if_needed`, migration 131) only
 * zeroes `free_daily_used`; it never grants credits. So once the pool is the
 * binding constraint, waiting for the reset changes nothing — and a surface
 * that still says "Resets daily · today at 5:34 pm" is promising relief that
 * will never arrive.
 *
 * Uncapped (paid / expired-window) users are never pool-bound in this sense:
 * they have no daily ceiling for the pool to be smaller than.
 */
export function isPoolBinding(view: CreditView): boolean {
  if (!view.isFreeDailyCapped || view.dailyLimit == null) return false;
  const dailyRoom = Math.max(0, view.dailyLimit - view.usedToday);
  return view.totalRemaining <= dailyRoom;
}

/**
 * How many MORE credits the user will actually be able to spend once the daily
 * window rolls — 0 when the reset changes nothing.
 *
 * This is the arithmetic behind every "resets today/tomorrow" string, and it is
 * deliberately derived rather than asserted. A daily roll sets `usedToday` to 0
 * and nothing else (`_roll_free_daily_if_needed`, migration 131 — it never
 * grants credits), so afterwards the user can spend
 *
 *     min(dailyLimit, totalRemaining)
 *
 * against today's `availableToday` = min(dailyLimit − usedToday, totalRemaining).
 * The difference IS the replenishment. Computing it this way collapses three
 * rules that used to live apart, and be applied inconsistently, into one:
 *
 *   - a FULL allowance gains nothing (min(50,300) − 50 = 0)
 *   - a DRAINED pool gains nothing (min(50,0) − 0 = 0)
 *   - a POOL-BOUND account gains nothing (min(50,2) − 2 = 0) ← the reported bug:
 *     "2/50 credits · resets today at 5:34 pm" on an account that had spent 298
 *     of its 300-credit grant. The reset was real; the replenishment was not.
 *
 * Uncapped accounts (paid, or an expired free window) have no daily roll at all,
 * so they always return 0 — their credits arrive by renewal or top-up, never by
 * the reset this module formats.
 */
export function creditsGainedAtReset(view: CreditView): number {
  if (!view.isFreeDailyCapped || view.dailyLimit == null) return 0;
  if (!view.resetsAtIso) return 0;
  const afterReset = Math.max(0, Math.min(view.dailyLimit, view.totalRemaining));
  return Math.max(0, afterReset - view.availableToday);
}

/**
 * The single gate for reset copy: true ONLY when the reset at `resetsAtIso`
 * will actually put usable credits back in the user's hands.
 *
 * Every surface that renders "resets today at …" / "resets tomorrow at …" MUST
 * key off this and not off a bare `resetsAtIso != null` — a non-null reset time
 * means "a daily window exists", never "credits are coming back".
 */
export function willResetReplenish(view: CreditView): boolean {
  return creditsGainedAtReset(view) > 0;
}

/**
 * DEPRECATED as the "credits are back" trigger. It detected the reset from the
 * raw stale shape (past `resetsAtIso` + old `usedToday`), which `/api/credits`
 * no longer returns — the endpoint now repairs an overdue reset on read
 * (migration 145 / `sync_free_daily`) and reports the moment via the
 * `daily_reset_just_restored` flag (server-side `creditsJustRestored`) instead.
 * Do NOT re-wire the message to this function; use the server flag. Kept only so
 * its existing unit tests continue to document the original detection logic.
 *
 * True when a free user who had EXHAUSTED their daily allowance has had that
 * allowance reset — i.e. their credits are genuinely "back" (PANDA-811). The DB
 * rolls the window lazily on the next spend (`_roll_free_daily_if_needed`), so
 * between the reset time passing and that next spend `/api/credits` used to still
 * report the window that just ended: a past `resetsAtIso` together with the old
 * `usedToday`. That exact shape was the reset moment we proactively messaged on.
 *
 * PANDA-1352: the gate must be full exhaustion (`usedToday >= dailyLimit` → 0
 * credits available today), NOT merely `usedToday > 0`. The old gate fired for ANY
 * returning user whose 24h window had elapsed — e.g. someone who spent only
 * 10/50 yesterday and reopened today — even though they were never blocked. That
 * showed "your credits are back" alongside a banner still advertising credits
 * remaining today, contradicting each other. A user who never hit the daily cap
 * has an active allowance and must never see the reset message. We also require
 * `totalRemaining > 0`, since a daily roll only restores the per-day cap, not
 * the lifetime pool — there is nothing to come "back" if the pool is drained.
 */
export function hasDailyCreditsReset(
  view: CreditView,
  now: number = Date.now(),
): boolean {
  if (!view.isFreeDailyCapped) return false;
  if (view.dailyLimit == null) return false;
  if (view.usedToday < view.dailyLimit) return false; // never hit the cap
  if (view.totalRemaining <= 0) return false; // nothing left to restore
  if (!view.resetsAtIso) return false;
  const resetMs = new Date(view.resetsAtIso).getTime();
  if (Number.isNaN(resetMs)) return false;
  return now >= resetMs;
}

/**
 * The 2-3 contextual "what next" actions for the credits-reset chat message
 * (PANDA-811). When a project title is known the suggestions are tied to that
 * last project (which converts far better than generic options); otherwise a
 * generic set is used. Each chip's `prompt` is what runs in the Build chat when
 * tapped.
 */
export function buildCreditsResetActions(
  projectTitle?: string | null,
): { action: string; prompt: string }[] {
  const title = (projectTitle ?? "").trim();
  if (title) {
    return [
      {
        action: `Continue where you left off on ${title}`,
        prompt: `Let's continue building ${title}. Suggest and make the most valuable next improvement.`,
      },
      {
        action: "Refine something from your last build",
        prompt: `Review the latest version of ${title} and refine it — tighten the design and fix any rough edges.`,
      },
      {
        action: "Try a new idea",
        prompt: `I want to try a new idea for ${title}. Help me brainstorm and add a fresh feature.`,
      },
    ];
  }
  return [
    {
      action: "Continue building your app",
      prompt:
        "Let's keep building. Suggest and make the most valuable next improvement.",
    },
    {
      action: "Start something new",
      prompt: "I want to start something new. Help me plan and build a fresh idea.",
    },
    {
      action: "Explore what Panda can build",
      prompt: "Show me a few impressive things you can build, with examples I can try.",
    },
  ];
}

/**
 * The single credit-balance line shown in every compact credit surface — nav
 * chips, low-credit banners, anywhere a "X/Y credits" state is rendered.
 * Canonical format (PANDA — "simplify credit balance copy"):
 *
 *   Full:      "50/50 credits"                                  (no reset)
 *   Partial:   "38/50 credits · resets today at 11:53 PM"
 *   Empty:     "0/50 credits · resets tomorrow at 11:53 PM"
 *   Pool-bound:"2/300 credits · upgrade to keep building"       (no reset)
 *
 * Rules: the reset clause is shown ONLY when that reset will actually hand the
 * user more credits (`willResetReplenish`), never says "full"/"empty"/"partially
 * used", and never appends "today" after the count. `today`/`tomorrow` and the
 * time come from the user's device timezone and locale via `formatResetTime`.
 *
 * `remaining`/`limit` key off the balance that actually gates the user, which is
 * NOT always the daily allowance. A free daily-capped account is normally gated
 * by its per-day room, so the badge reads "38/50" — but once the banked pool
 * falls to or below that room the POOL is the ceiling, and quoting the daily cap
 * invents a denominator the user will never reach again. That was the reported
 * mismatch: an account that had spent 298 of its 300-credit grant was shown
 * "2/50 credits", the 50 implying a fresh daily allowance rather than the 2
 * credits it actually had left of 300. Pool-bound accounts therefore render
 * against the pool — "2/300 credits" — and paid accounts always do.
 *
 * When a top-up pushes the balance above its grant (remaining > limit) the
 * denominator is dropped — "1450/650 credits" would read as exceeding a max —
 * so it renders just "1450 credits".
 */
export interface CreditBadge {
  /** "38/50 credits" (or "1450 credits" when the denominator is dropped). */
  count: string;
  /**
   * The count with the word "credits" dropped — "38/50" (or "1450" when the
   * denominator is dropped). For the narrow mobile nav, where the full line
   * wraps onto two lines and crowds the logo/avatar (PANDA — "compact mobile
   * credit balance"): the header shows this instead and moves the reset clause
   * to wider surfaces (desktop chip, Settings → Billing).
   */
  countShort: string;
  /**
   * "resets today at 11:53 PM" — present ONLY when the reset genuinely
   * replenishes. null whenever waiting would not help, so no surface can
   * promise credits that aren't coming.
   */
  reset: string | null;
  /**
   * What to say INSTEAD when credits are what's limiting the user and no reset
   * will fix it: "upgrade to keep building" (free grant spent) / "top up to keep
   * building" (paid pool spent). null when there's nothing to warn about.
   */
  depletion: string | null;
  /**
   * The clause rendered after " · " — `reset` when credits are coming back,
   * `depletion` when they are not, null when neither applies. Renderers should
   * use THIS rather than picking between the two themselves.
   */
  detail: string | null;
  /** The full line: count, plus " · " + detail when present. */
  text: string;
  /** True when the balance is at (or above) the limit. */
  isFull: boolean;
  /** True when the displayed reset will actually add usable credits. */
  replenishes: boolean;
  /**
   * Which balance `remaining`/`limit` describe: the daily allowance, or the
   * banked pool (paid accounts, and free accounts the pool now gates). Exposed
   * so tests and the copy-integrity check can assert the denominator is the
   * one that actually binds. See lib/credits/copy-integrity.ts.
   */
  basis: "daily" | "pool";
  remaining: number;
  limit: number;
}

export function formatCreditBadge(view: CreditView): CreditBadge {
  const replenishes = willResetReplenish(view);
  const poolBinding = isPoolBinding(view);

  // Quote the ceiling the user is actually up against. The daily cap only
  // deserves the denominator while it is the smaller of the two constraints.
  const basis: "daily" | "pool" =
    view.isFreeDailyCapped && !poolBinding ? "daily" : "pool";
  const remaining = basis === "daily" ? view.availableToday : view.totalRemaining;
  const limit = basis === "daily" ? (view.dailyLimit ?? 0) : view.total;

  // Drop the denominator when it isn't a meaningful ceiling: a top-up balance
  // above the grant (paid), or an unresolved/zero limit.
  const hasMeaningfulLimit = limit > 0 && remaining <= limit;
  const countShort = hasMeaningfulLimit ? `${remaining}/${limit}` : `${remaining}`;
  const count = `${countShort} credits`;

  const isFull = limit > 0 && remaining >= limit;
  const resetPhrase = view.resetsAtIso ? formatResetTime(view.resetsAtIso) : null;
  const reset = replenishes && resetPhrase ? `resets ${resetPhrase}` : null;

  // Only speak up when credits are genuinely the constraint. A free account
  // sitting on a full allowance and a healthy pool gets no clause at all — same
  // quiet chip as before; it is the pool-bound and drained states that were
  // being handed a false reset promise and now get the honest next step. Free
  // accounts have no one-off top-up path (see components/chat/credit-limit-bar),
  // so theirs points at upgrading.
  let depletion: string | null = null;
  if (!reset) {
    if (view.isFreeDailyCapped && poolBinding) {
      depletion = "upgrade to keep building";
    } else if (!view.isFreeDailyCapped && view.totalRemaining <= 0) {
      // Keyed on the PLAN, not on the daily-cap state. Keying it on
      // `isFreeDailyCapped` sent a whole cohort to a path that does not exist for
      // them: a free account whose window is disarmed — every ex-paid user
      // (applySubscriptionGrant NULLs the window on upgrade and cancellation
      // never re-arms it), every expired $1 trial, every pre-migration-131 row —
      // is `isFreeDailyCapped === false`, so it landed in this branch and was told
      // to "top up". Free accounts have no one-off top-up path at all, as the note
      // above says; theirs must point at upgrading.
      depletion =
        view.plan === "free"
          ? "upgrade to keep building"
          : "top up to keep building";
    }
  }

  const detail = reset ?? depletion;
  const text = detail ? `${count} · ${detail}` : count;

  return {
    count,
    countShort,
    reset,
    depletion,
    detail,
    text,
    isFull,
    replenishes,
    basis,
    remaining,
    limit,
  };
}

/**
 * Day-anchored reset time in the user's local timezone, e.g. "today at 8:00 PM",
 * "tomorrow at 8:00 PM", or "on Fri, Jun 27 at 8:00 PM". We never return a bare
 * time: a reset time with no day anchor leaves the user unable to tell whether
 * it is minutes or a full day away (PANDA-1351). The raw ISO comes from the DB;
 * the browser locale does the conversion. Returns null for missing/invalid input.
 */
export function formatResetTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    let d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    // The daily cap rolls every 24h and is repaired LAZILY (sync_free_daily,
    // migration 145 — it only advances on read/spend), so a freshly-read value
    // can momentarily sit in the PAST before the roll lands. We must never name
    // a reset the user's own clock has already passed — that's the reported bug
    // ("resets today at 4:42 PM" shown at 8:23 PM). Advance the instant in whole
    // LOCAL days (preserving the local time-of-day across DST) until it's in the
    // future, mirroring the DB's own 24h roll, so the copy always points at the
    // NEXT reset in the user's device timezone. When the allowance is genuinely
    // full the caller hides this copy, so a legitimately-past boundary is never
    // routed here. Bounded by real staleness (a day or two); the guard caps it.
    for (let i = 0; i < 400 && d.getTime() <= now.getTime(); i++) {
      d = new Date(
        d.getFullYear(),
        d.getMonth(),
        d.getDate() + 1,
        d.getHours(),
        d.getMinutes(),
        d.getSeconds(),
        d.getMilliseconds(),
      );
    }
    // 12-hour clock with AM/PM, matching the product copy ("resets today at
    // 4:42 PM" / "…at 12:00 AM"). We force hour12 rather than inheriting the
    // locale default because a browser's hour cycle does NOT reliably follow the
    // OS 12/24-hour setting (e.g. a 12-hour Mac still rendered "22:52"); forcing
    // it keeps the copy consistent for every user. The zone is still the device's
    // local timezone — only the 12/24 presentation is pinned.
    const time = new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
    const startOf = (x: Date) =>
      new Date(x.getFullYear(), x.getMonth(), x.getDate());
    const diffDays = Math.round(
      (startOf(d).getTime() - startOf(now).getTime()) / 86400000,
    );
    if (diffDays <= 0) return `today at ${time}`;
    if (diffDays === 1) return `tomorrow at ${time}`;
    const date = new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    }).format(d);
    return `on ${date} at ${time}`;
  } catch {
    return null;
  }
}

/**
 * Bind the meridiem to the clock time so a narrow column can never orphan it.
 *
 * The reported layout bug: in the chat panel's low-credit banner the message
 * column is what's left after a `shrink-0` "Upgrade to keep building" CTA, and
 * "Resets tomorrow at 8:47 PM." broke at the space before the meridiem —
 * leaving "PM." alone on its own line.
 *
 * DISPLAY ONLY, and deliberately NOT folded into formatResetTime: that string
 * is also embedded in plain-text chat messages (lib/chat/failed-turn.ts) and
 * asserted verbatim in tests, so a U+00A0 there would plant an invisible
 * non-ASCII character into copy that gets read, logged, and diffed. Callers
 * that RENDER the phrase opt in; callers that emit text do not.
 *
 * A no-op where the runtime already emits U+202F (narrow no-break space) before
 * AM/PM, as newer ICU builds do — that character is already non-breaking, and
 * the pattern only matches an ordinary space.
 */
export function bindMeridiem(phrase: string): string;
export function bindMeridiem(phrase: null | undefined): null;
export function bindMeridiem(phrase: string | null | undefined): string | null;
export function bindMeridiem(phrase: string | null | undefined): string | null {
  if (!phrase) return null;
  // Written as an escape on purpose: a literal non-breaking space is
  // invisible in review and indistinguishable from a plain one in a diff.
  return phrase.replace(/ (AM|PM)\b/gi, "\u00A0$1");
}

/**
 * Bind the whole clock instant — "at 6:29 PM" — into one unbreakable chunk.
 *
 * The stronger sibling of {@link bindMeridiem}, for banners whose copy column
 * is guaranteed a width floor. bindMeridiem only stops "PM." stranding on its
 * own line; it leaves the join before the clock breakable, so a column a few
 * pixels too narrow still splits "Next reset tomorrow at" from "6:29 PM." —
 * the reported credit-limit-bar wrap. Binding the join too makes that split
 * impossible at ANY width, while "Next reset" and the day anchor stay
 * breakable so the phrase can still reflow.
 *
 * Only safe where the copy column cannot become narrower than the bound chunk
 * (~62px at 12px Inter), since a non-breaking run longer than its line
 * overflows rather than wraps. components/chat/credit-limit-bar reserves that
 * floor with `basis-52`; the ChatPanel low-credit banner deliberately does NOT
 * use this helper — its column is narrower and it relies on `text-balance`
 * plus bindMeridiem alone.
 *
 * DISPLAY ONLY, for the same reason bindMeridiem is: these strings are also
 * embedded in plain-text chat messages and asserted verbatim in tests, so the
 * U+00A0 must never reach lib/chat/failed-turn.ts or the copy helpers.
 */
export function bindResetPhrase(phrase: string): string;
export function bindResetPhrase(phrase: null | undefined): null;
export function bindResetPhrase(phrase: string | null | undefined): string | null;
export function bindResetPhrase(phrase: string | null | undefined): string | null {
  if (!phrase) return null;
  // Anchored on the digit that starts the clock, so only the "at" that joins a
  // day anchor to a TIME is bound — never an "at" that a locale-formatted date
  // part might contain. Written as an escape on purpose: a literal
  // non-breaking space is invisible in review.
  return bindMeridiem(phrase).replace(/\bat (?=\d)/g, "at\u00A0");
}

/**
 * Split a banner message into a lead sentence and whatever follows.
 *
 * The low-credit banner reads as a headline plus a quieter detail line rather
 * than one undifferentiated paragraph — which is what let a long variant
 * ("Credits are almost out. You may not have enough credits for another full
 * request.") fill the pane as a wall of same-weight text. The copy helper
 * (lowCreditBannerCopy) returns ONE string per variant, so the split happens
 * here at render time instead of forking every message in the experiment.
 *
 *   "Credits are almost out. You may not have…"
 *     → lead: "Credits are almost out."
 *       rest: "You may not have…"
 *
 * Single-sentence messages ("Credits are running low.") return an empty `rest`,
 * and the caller then shows only the reset phrase as the detail line.
 *
 * Deliberately conservative: it splits on the FIRST sentence terminator that is
 * followed by whitespace and has a plausible amount of text before it. A short
 * lead guard keeps an abbreviation or a decimal from being mistaken for the end
 * of a sentence — none of today's copy contains one, but the messages are
 * experiment-authored and this runs on whatever they become.
 */
export function splitLeadSentence(message: string): {
  lead: string;
  rest: string;
} {
  const trimmed = (message ?? "").trim();
  if (!trimmed) return { lead: "", rest: "" };
  // `[\s\S]` rather than the `s` (dotAll) flag: that flag needs an es2018+
  // target, and this tsconfig targets lower.
  const m = trimmed.match(/^([\s\S]{12,}?[.!?])\s+(\S[\s\S]*)$/);
  if (!m) return { lead: trimmed, rest: "" };
  return { lead: m[1]!, rest: m[2]! };
}
